import { Head, Link, router } from '@inertiajs/react'
import { ArrowLeft, BookOpen, MessageCircle, PhoneCall, ShieldCheck, Star } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button, buttonClasses } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import type { Breadcrumb } from '@/types/shared'

type PlayerDetail = {
  id: string
  name: string | null
  teamName?: string | null
  prefecture?: string | null
  prefectureId?: number | null
  birthday?: string | null
  height?: number | null
  fatherHeight?: number | null
  motherHeight?: number | null
  dominantFoot?: string | null
  dominantFootLabel?: string | null
  boarding?: boolean | null
  fiftyMeterTime?: number | null
  comment?: string | null
  positions: string[]
  videoUrls: string[]
  updatedAt: string
}

type ChatroomSummary = {
  id: string
  lastMessageAt?: string | null
  lastMessagePreview?: string | null
}

type TeamChatroomSummary = {
  id: string
  teamName: string
  lastMessageAt?: string | null
  lastMessagePreview?: string | null
}

type AcademicGrades = {
  periods: Array<{
    schoolYear: number
    term: number
    label: string
  }>
  subjects: Array<{
    value: string
    label: string
  }>
  grades: Array<{
    schoolYear: number
    term: number
    subject: string
    rating: number | null
  }>
}

export type SchoolPlayerShowProps = {
  player: PlayerDetail
  academicGrades: AcademicGrades
  chatroom: ChatroomSummary | null
  teamChatroom: TeamChatroomSummary | null
  contactPath: string
  teamContactPath: string | null
  breadcrumbs: Breadcrumb[]
}

export default function SchoolPlayerShow({
  player,
  academicGrades,
  chatroom,
  teamChatroom,
  contactPath,
  teamContactPath,
  breadcrumbs,
}: SchoolPlayerShowProps) {
  const startConversation = () => {
    router.post(contactPath, {}, { preserveScroll: true })
  }

  const startTeamConversation = () => {
    if (!teamContactPath) return
    router.post(teamContactPath, {}, { preserveScroll: true })
  }

  return (
    <AppShell>
      <Head title={player.name ? `${player.name}｜選手詳細` : '選手詳細'} />
      <PageHeader
        title={player.name ?? '選手詳細'}
        description="プロフィールの詳細を確認し、気になる点があればすぐにコンタクトを取りましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link href="/school/players">
              <ArrowLeft className="mr-2 h-4 w-4" />
              一覧に戻る
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)]">
        <Card padding="lg" className="space-y-10">
          <section className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white/50">最新更新</p>
              <h2 className="text-2xl font-semibold text-white">{player.name ?? '名前未登録'}</h2>
              <div className="flex flex-wrap items-center gap-2 text-sm text-white/60">
                <span>{player.teamName ?? '所属チーム未登録'}</span>
                <span>·</span>
                <span>{player.prefecture ?? '所在地未登録'}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {player.positions.length ? (
                  player.positions.map((position) => (
                    <Badge key={position} variant="default">
                      {position}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="outline">ポジション未設定</Badge>
                )}
                <Badge variant="outline">{player.dominantFootLabel || '利き足未設定'}</Badge>
                <Badge variant={player.boarding ? 'success' : 'outline'}>
                  {player.boarding == null ? '下宿未登録' : player.boarding ? '下宿可能' : '下宿不可'}
                </Badge>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-4 text-sm text-white/70 shadow-soft">
              <p className="text-xs uppercase tracking-wide text-white/40">フィジカル概要</p>
              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                <Stat label="身長" value={formatHeight(player.height)} />
                <Stat label="50m走" value={formatSprint(player.fiftyMeterTime)} />
                <Stat label="父親の身長" value={formatHeight(player.fatherHeight)} />
                <Stat label="母親の身長" value={formatHeight(player.motherHeight)} />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-white">プロフィール情報</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoBlock
                title="生年月日"
                value={player.birthday ? formatDate(player.birthday) : '未登録'}
              />
              <InfoBlock title="利き足" value={player.dominantFootLabel || '未登録'} />
              <InfoBlock
                title="下宿"
                value={player.boarding == null ? '未登録' : player.boarding ? '可能' : '不可'}
              />
              <InfoBlock title="更新日時" value={formatDateTime(player.updatedAt)} />
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-white">第三者コメント</h3>
            {player.comment ? (
              <Card variant="muted">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/80">{player.comment}</p>
              </Card>
            ) : (
              <EmptyState
                title="コメントはまだありません"
                description="選手にプレースタイルや強みを依頼すると、より具体的なコミュニケーションが可能になります。"
                icon={<Star className="h-6 w-6" />}
              />
            )}
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-white">動画リンク</h3>
            {player.videoUrls.length === 0 ? (
              <EmptyState
                title="動画が登録されていません"
                description="最新のハイライト動画を依頼すると、プレーのイメージを掴みやすくなります。"
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {player.videoUrls.map((url, index) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white transition hover:border-white/20 hover:bg-white/10"
                  >
                    <span className="text-xs text-white/50">動画 {index + 1}</span>
                    <span className="line-clamp-2 break-all text-sm text-brand-100 group-hover:text-brand-50">{url}</span>
                  </a>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-white">学業成績</h3>
            <AcademicGradesTable academicGrades={academicGrades} />
          </section>
        </Card>

        <div className="space-y-6">
          <Card padding="lg" className="space-y-4">
            <h3 className="text-lg font-semibold text-white">サッカー日記</h3>
            <p className="text-sm text-white/70">
              日記の継続性から選手の人間性（継続力・真面目さ）を確認できます。
            </p>
            <Link
              href={`/school/players/${player.id}/soccer_diaries`}
              className={buttonClasses({ variant: 'primary', size: 'sm' })}
            >
              <BookOpen className="mr-2 h-4 w-4" />
              日記を見る
            </Link>
          </Card>

          <Card padding="lg" className="space-y-4 border-brand-500/40 bg-brand-500/5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand-200" />
              <h3 className="text-lg font-semibold text-white">まずはチームコーチに連絡（推奨）</h3>
            </div>
            <p className="text-sm text-white/70">
              選手に直接連絡する前に、所属チームのコーチに相談することで、より良いマッチングに繋がります。
            </p>

            {teamChatroom ? (
              <div className="space-y-4">
                <Button asChild size="lg">
                  <Link href={`/school/team_chatrooms/${teamChatroom.id}`}>
                    {teamChatroom.teamName} のコーチとのチャットを開く
                    <MessageCircle className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Card variant="muted" className="space-y-2 text-sm text-white/70">
                  <p className="text-xs font-semibold text-white/50">直近のメッセージ</p>
                  <p className="line-clamp-3">{teamChatroom.lastMessagePreview ?? 'メッセージ履歴がありません。'}</p>
                  {teamChatroom.lastMessageAt ? (
                    <time className="text-xs text-white/40">最終更新 {formatDateTime(teamChatroom.lastMessageAt)}</time>
                  ) : null}
                </Card>
              </div>
            ) : (
              <Button
                size="lg"
                onClick={startTeamConversation}
                disabled={!teamContactPath}
              >
                {player.teamName ? `${player.teamName} のコーチに連絡` : 'チームコーチに連絡'}
                <MessageCircle className="ml-2 h-4 w-4" />
              </Button>
            )}
          </Card>

          <Card padding="lg" className="space-y-4">
            <h3 className="text-lg font-semibold text-white">選手に直接連絡</h3>
            <p className="text-sm text-white/70">
              選手に質問を投げかけたり、訪問日程を提示してコミュニケーションを進めましょう。
            </p>

            {chatroom ? (
              <div className="space-y-4">
                <Button asChild size="lg">
                  <Link href={`/school/chatrooms/${chatroom.id}`}>
                    チャットを開く
                    <MessageCircle className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Card variant="muted" className="space-y-2 text-sm text-white/70">
                  <p className="text-xs font-semibold text-white/50">直近のメッセージ</p>
                  <p className="line-clamp-3">{chatroom.lastMessagePreview ?? 'メッセージ履歴がありません。'}</p>
                  {chatroom.lastMessageAt ? (
                    <time className="text-xs text-white/40">最終更新 {formatDateTime(chatroom.lastMessageAt)}</time>
                  ) : null}
                </Card>
              </div>
            ) : (
              <div className="space-y-4">
                <Button size="lg" variant="secondary" onClick={startConversation}>
                  選手にメッセージを送る
                  <MessageCircle className="ml-2 h-4 w-4" />
                </Button>
                <Card variant="muted" className="space-y-3 text-sm text-white/70">
                  <p className="text-xs font-semibold uppercase tracking-wide text-white/40">ヒント</p>
                  <ul className="space-y-2 list-disc list-inside">
                    <li>自己紹介と学校の特徴を簡潔に伝えましょう。</li>
                    <li>プレー動画への感想や次のステップを提案すると返信率が向上します。</li>
                    <li>訪問候補日を複数提示すると日程調整がスムーズです。</li>
                  </ul>
                </Card>
              </div>
            )}
          </Card>

          <Card padding="lg" className="space-y-4">
            <h3 className="text-lg font-semibold text-white">連絡メモ</h3>
            <p className="text-sm text-white/70">
              電話やオンライン面談など、複数の連絡手段を提案すると意思決定が加速します。
            </p>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
              <p className="flex items-center gap-2 text-sm text-white">
                <PhoneCall className="h-4 w-4 text-brand-200" />
                連絡時に伝えたいポイント
              </p>
              <ul className="mt-3 list-disc space-y-1 pl-5">
                <li>チームのビジョンや育成方針</li>
                <li>寮・学費サポートなどの提供可否</li>
                <li>訪問またはオンライン面談の候補日時</li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}

type AcademicGradesTableProps = {
  academicGrades: AcademicGrades
}

function AcademicGradesTable({ academicGrades }: AcademicGradesTableProps) {
  const gradeByCell = new Map(
    academicGrades.grades.map((grade) => [cellKey(grade.schoolYear, grade.term, grade.subject), grade.rating])
  )

  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[560px] table-fixed border-collapse text-sm">
        <thead className="bg-white/5">
          <tr>
            <th className="w-32 border-b border-white/10 px-3 py-3 text-left font-medium text-white/60">
              期間
            </th>
            {academicGrades.subjects.map((subject) => (
              <th
                key={subject.value}
                className="border-b border-white/10 px-3 py-3 text-center font-medium text-white/60"
              >
                {subject.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {academicGrades.periods.map((period) => (
            <tr key={`${period.schoolYear}-${period.term}`}>
              <th className="border-b border-white/10 px-3 py-3 text-left font-medium text-white">
                {period.label}
              </th>
              {academicGrades.subjects.map((subject) => {
                const rating = gradeByCell.get(cellKey(period.schoolYear, period.term, subject.value))

                return (
                  <td
                    key={subject.value}
                    className="border-b border-white/10 px-3 py-3 text-center font-semibold text-white"
                  >
                    {rating ?? '-'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function cellKey(schoolYear: number, term: number, subject: string) {
  return `${schoolYear}-${term}-${subject}`
}

type StatProps = {
  label: string
  value: string
}

function Stat({ label, value }: StatProps) {
  return (
    <div>
      <p className="text-xs text-white/40">{label}</p>
      <p className="text-sm font-semibold text-white">{value}</p>
    </div>
  )
}

type InfoBlockProps = {
  title: string
  value: string
}

function InfoBlock({ title, value }: InfoBlockProps) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
      <p className="text-xs text-white/40">{title}</p>
      <p className="mt-2 font-semibold text-white">{value}</p>
    </div>
  )
}

function formatHeight(value?: number | null) {
  if (value == null) return '未登録'
  return `${Number(value)} cm`
}

function formatSprint(value?: number | null) {
  if (value == null) return '未登録'
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return '未登録'
  return `${numeric.toFixed(1)} 秒`
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
